import { useState, useEffect, useCallback, useRef } from 'react';
import { SWEEPSTAKES_MOCK_DATA } from '../core/Data/SweepstakesMockData';
import { mapSweepstakesData } from '../components/Sweepstakes/utils/SweepstakesDataMapper';

// Custom hook for executing async functions in useEffect with cleanup
const useAsyncEffect = (asyncFunction, dependencies) => {
  useEffect(() => {
    let isCancelled = false;

    const executeAsync = async () => {
      if (!isCancelled) {
        await asyncFunction();
      }
    };

    executeAsync();

    return () => {
      isCancelled = true;
    };
  }, dependencies);
};

// TODO: Remove after GraphQL migration is complete (2025Q4S6)
const fetchSweepstakesFromAPI = async () => {
  // Get THDCustomer object from cookie or window object
  const getTHDCustomer = () => {
    try {
      // Try to get from window.THDCustomer first (if available)
      if (window.THDCustomer) {
        return window.THDCustomer.default;
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  const thdCustomer = getTHDCustomer();

  if (!thdCustomer) {
    throw new Error('THDCustomer not found');
  }

  const { userID, svocID, jwt } = thdCustomer;

  // Switch hosts. If url contains 'hd-qa74', use `https://hd-qa74.homedepotdev.com`.
  // If url contains 'hd-qa71', use `https://hd-qa71.homedepotdev.com`.
  // Otherwise, use production host, `https://homedepot.com`
  const currentHost = window.location.hostname;
  let apiHost = 'https://www.homedepot.com';

  if (currentHost.includes('hd-qa74')) {
    apiHost = 'https://hd-qa74.homedepotdev.com';
  } else if (currentHost.includes('hd-qa71')) {
    apiHost = 'https://hd-qa71.homedepotdev.com';
  }

  // Construct API URL
  const endpoint = 'challenges/challengesInfo?challengeType=sweepstakes';
  const apiUrl = `${apiHost}/b2b/user/account/${userID}/customer/${svocID}/${endpoint}`;

  const response = await fetch(apiUrl, {
    method: 'GET',
    headers: {
      channelId: '1',
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies
  });

  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }

  const data = await response.json();

  // Return the first challenge from the array along with userProfile
  if (data.challenges && data.challenges.length > 0) {
    // If challengeId === da889334-a50a-4c09-876d-8bc0bca691cb, return that
    const challengeStr = 'da889334-a50a-4c09-876d-8bc0bca691cb';
    // const challengeStr = 'a123e8e7-6543-423c-aaa0-ebac0469492f';
    // const challengeStr = 'd9b2db7e-bd34-49c7-9401-b844a1d153ef';
    const specificChallenge = data.challenges.find((challenge) => challenge.challengeId === challengeStr);
    if (specificChallenge) {
      return { challenge: specificChallenge, userProfile: data.userProfile };
    }

    // Find first challenge with challengeMetaData.type === 'SWEEPSTAKES' and challengeStatus === 'ACTIVE'
    const activeSweepstakesChallenge = data.challenges.find(
      (challenge) => challenge?.challengeMetaData?.type === 'SWEEPSTAKES'
      && challenge?.challengeStatus === 'ACTIVE'
    );
    if (activeSweepstakesChallenge) {
      return { challenge: activeSweepstakesChallenge, userProfile: data.userProfile };
    }

    // Otherwise, return the first challenge
    return { challenge: data.challenges[0], userProfile: data.userProfile };
  }

  throw new Error('No challenges found in response');
};

/*
  New function acceptSweepstakesFromAPI
  Description: Called from useFetchSweepstakesAccept to accept sweepstakes via REST API.
    Body properties come from the sweepstakes data
  Method: PUT
  Endpoint: /b2b/user/account/{userID}/customer/{svocID}/challenges/accept
  Body: {
    acceptChallengeRequest: {
      "challengeId": "<challengeId>",
      "customerChallengeId": "<customerChallengeId>" // optional, undefined if not available
      "isV2": "<v2Challenge>" // optional, true/false, false if not available
    }
  }
  Headers: channelId: '1', 'Content-Type': 'application/json'
*/

const acceptSweepstakesFromAPI = async ({ challengeId, customerChallengeId, isV2 } = {}) => {
  // Get THDCustomer object from cookie or window object
  const getTHDCustomer = () => {
    try {
      // Try to get from window.THDCustomer first (if available)
      if (window.THDCustomer) {
        return window.THDCustomer.default;
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  const thdCustomer = getTHDCustomer();

  if (!thdCustomer) {
    throw new Error('THDCustomer not found');
  }

  const { userID, svocID } = thdCustomer;

  // Switch hosts. If url contains 'hd-qa74', use `https://hd-qa74.homedepotdev.com`.
  // If url contains 'hd-qa71', use `https://hd-qa71.homedepotdev.com`.
  // Otherwise, use production host, `https://homedepot.com`
  const currentHost = window.location.hostname;
  let apiHost = 'https://www.homedepot.com';

  if (currentHost.includes('hd-qa74')) {
    apiHost = 'https://hd-qa74.homedepotdev.com';
  } else if (currentHost.includes('hd-qa71')) {
    apiHost = 'https://hd-qa71.homedepotdev.com';
  }

  // Construct API URL
  const endpoint = 'challenges/accept';
  const apiUrl = `${apiHost}/b2b/user/account/${userID}/customer/${svocID}/${endpoint}`;

  const body = {
    acceptChallengeRequest: {
      challengeId,
      ...(customerChallengeId && { customerChallengeId }),
      ...(typeof isV2 === 'boolean' && { isV2 }),
    },
  };

  const response = await fetch(apiUrl, {
    method: 'PUT',
    headers: {
      channelId: '1',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    credentials: 'include', // Include cookies
  });

  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }

  try {
    return await response.json();
  } catch (error) {
    return { success: true };
  }
};

export const useFetchSweepstakes = ({
  providedData,
  userOptInStatus = 'notOptedIn',
  skip = false,
  useRestAPI = false
} = {}) => {
  const [challengeData, setChallengeData] = useState(null);
  const [userProfileData, setUserProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const inFlightRequestRef = useRef(null);
  const lastRequestKeyRef = useRef(null);

  const getRequestKey = useCallback(() => {
    const providedDataKey = providedData
      ? (providedData.challengeId || providedData.id || 'provided-data')
      : 'no-provided-data';
    const apiMode = useRestAPI ? 'rest' : 'mock';

    return `${apiMode}|${userOptInStatus}|${providedDataKey}`;
  }, [providedData, useRestAPI, userOptInStatus]);

  const fetchSweepstakesData = useCallback(async ({ force = false } = {}) => {
    if (skip) {
      setIsLoading(false);
      return;
    }

    const requestKey = getRequestKey();
    if (!force && inFlightRequestRef.current && lastRequestKeyRef.current === requestKey) {
      await inFlightRequestRef.current;
      return;
    }

    setIsLoading(true);
    setHasError(false);

    lastRequestKeyRef.current = requestKey;

    const requestPromise = (async () => {
      // REST Function called here, sets data & states accordingly, and returns
      // TODO: Remove after GraphQL migration is ready (2025Q4S6)
      const isCallingRestFuction = useRestAPI;
      if (isCallingRestFuction) {
        try {
          const apiData = await fetchSweepstakesFromAPI();
          const remappedData = mapSweepstakesData(apiData.challenge);
          setChallengeData(remappedData);
          setUserProfileData(apiData.userProfile || null);
          setHasError(false);
          setIsLoading(false);
          return;
        } catch (error) {
          setHasError(true);
          setIsLoading(false);
          return;
        }
      }

      // Leave the following try-catch-finally structure as is for simulating async data fetch

      try {
        // Simulate network delay - use shorter delay in test environment
        const delay = process.env.NODE_ENV === 'test' ? 100 : 1500;
        await new Promise((resolve) => {
          setTimeout(resolve, delay);
        });

        // Use provided data if available, otherwise get mock data based on user status
        let responseData;
        let isAlreadyMapped = false;
        if (providedData) {
          // Check if providedData needs mapping (has challengeId field) or is already mapped
          responseData = providedData;
          isAlreadyMapped = !providedData.challengeId;
        } else {
          // Get mock data based on status
          switch (userOptInStatus) {
          case 'notOptedIn':
            responseData = SWEEPSTAKES_MOCK_DATA.notOptedIn;
            break;
          case 'partiallyCompleted':
            responseData = SWEEPSTAKES_MOCK_DATA.partiallyCompleted;
            break;
          case 'fullyCompleted':
            responseData = SWEEPSTAKES_MOCK_DATA.fullyCompleted;
            break;
          default:
            responseData = SWEEPSTAKES_MOCK_DATA.notOptedIn;
          }
        }

        // Map data at setData point (only if not already mapped)
        const finalData = isAlreadyMapped ? responseData : mapSweepstakesData(responseData);
        setChallengeData(finalData);
      } catch (error) {
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    })();

    inFlightRequestRef.current = requestPromise;

    try {
      await requestPromise;
    } finally {
      if (inFlightRequestRef.current === requestPromise) {
        inFlightRequestRef.current = null;
      }
    }
  }, [getRequestKey, providedData, skip, useRestAPI, userOptInStatus]);

  useAsyncEffect(fetchSweepstakesData, [fetchSweepstakesData]);

  const refresh = useCallback(() => {
    fetchSweepstakesData({ force: true });
  }, [fetchSweepstakesData]);

  return {
    challengeData,
    userProfileData,
    isLoading,
    hasError,
    refresh,
  };
};

export const useFetchSweepstakesAccept = ({ forceError = false, useRestAPI = false } = {}) => {
  const [acceptData, setAcceptData] = useState(null);
  const [isLoadingAccept, setIsLoadingAccept] = useState(false);
  const [acceptError, setAcceptError] = useState(false);

  const acceptSweepstakes = useCallback(async ({ challengeId, customerChallengeId, isV2 } = {}) => {
    setIsLoadingAccept(true);
    setAcceptError(false);

    if (useRestAPI) {
      try {
        const data = await acceptSweepstakesFromAPI({ challengeId, customerChallengeId, isV2 });
        const responseData = {
          success: true,
          message: 'Successfully opted into sweepstakes',
          data,
        };
        setAcceptData(responseData);
        return responseData;
      } catch (error) {
        setAcceptError(true);
        const errorResponse = { success: false, error: error.message || 'API error occurred' };
        setAcceptData(errorResponse);
        return errorResponse;
      } finally {
        setIsLoadingAccept(false);
      }
    }

    try {
      // Simulate accepting sweepstakes
      await new Promise((resolve, reject) => {
        setTimeout(() => {
          if (forceError) {
            reject(new Error('Simulated network error for testing'));
          } else {
            resolve();
          }
        }, 1000);
      });

      const responseData = {
        success: true,
        message: 'Successfully opted into sweepstakes',
      };

      setAcceptData(responseData);
      return responseData;
    } catch (error) {
      setAcceptError(true);
      const errorResponse = { success: false, error: error.message || 'Network error occurred' };
      setAcceptData(errorResponse);
      return errorResponse;
    } finally {
      setIsLoadingAccept(false);
    }
  }, [forceError, useRestAPI]);

  return {
    acceptData,
    isLoadingAccept,
    acceptError,
    acceptSweepstakes,
  };
};

export const useUpdateSweepstakes = (baseData) => {
  const [localDataUpdates, setLocalDataUpdates] = useState(null);
  const baseDataRef = useRef(baseData);

  // Keep ref in sync with baseData
  useEffect(() => {
    baseDataRef.current = baseData;
  }, [baseData]);

  // Apply local updates to base data
  const effectiveData = localDataUpdates ? { ...baseData, ...localDataUpdates } : baseData;

  const stepUpdater = (step, updates) => {
    const stepUpdate = updates.steps.find((updateStep) => updateStep.id === step.id);
    return stepUpdate ? { ...step, ...stepUpdate } : step;
  };

  // Function to update sweepstakes data at runtime
  const updateSweepstakesData = useCallback((updates) => {
    setLocalDataUpdates((prevUpdates) => {
      const currentBaseData = baseDataRef.current;
      const currentData = prevUpdates ? { ...currentBaseData, ...prevUpdates } : currentBaseData;
      const updatedData = { ...currentData };
      const processStep = (step) => stepUpdater(step, updates);

      // Apply all updates dynamically
      Object.keys(updates).forEach((key) => {
        if (key === 'completedSteps') {
          // Special handling for completedSteps to ensure it only increases
          updatedData.completedSteps = Math.max(
            updatedData.completedSteps || 0,
            updates.completedSteps
          );
        } else if (key === 'steps' && updates.steps && updatedData.steps) {
          // Special handling for steps array
          updatedData.steps = updatedData.steps.map(processStep);
        } else {
          // Direct assignment for all other fields (ctaTitle, isOptedIn, status, etc.)
          updatedData[key] = updates[key];
        }
      });

      // Return only the updates (delta from base data)
      const deltaUpdates = {};
      Object.keys(updatedData).forEach((key) => {
        if (JSON.stringify(updatedData[key]) !== JSON.stringify(currentBaseData?.[key])) {
          deltaUpdates[key] = updatedData[key];
        }
      });

      return Object.keys(deltaUpdates).length > 0 ? deltaUpdates : null;
    });
  }, []);

  // Reset updates when base data changes
  useEffect(() => {
    setLocalDataUpdates(null);
  }, [baseData]);

  return {
    effectiveData,
    updateSweepstakesData
  };
};